import { FunctionComponent, useState } from 'react'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader'
import DialogBody from '@/components/_custom/DialogBody/DialogBody'
import styles from './deleteOccasionalSlotDialog.module.scss'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Close, Delete } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client'
import { DELETE_OCCASIONAL_SLOT } from '@/modules/API/slot/occasionalSlotHandler'
import UserInput from '@/components/_custom/UserInput/userInput'
import { SelectOptionDeprecated, SelectOptionsDeprecated } from '@/types/Select'
import SelectMultiple from '@/components/_custom/Selector/selectMultiple/selectMultiple'
import { CalendarEventType } from '@/types/Calendar'
import TextArea from '@/components/_custom/TextArea/textArea'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { usePredefinedMessage } from '@/container/daycare/daycareProfile/customisation/predefinedMessage/usePredefinedMessage'
import { DaycarePredefinedMessageType } from '@/types/Daycare'
import { useCurrentDaycareInfos } from '@/hooks/useCurrentDaycareInfos'
import { OccasionalSlot } from '@/types/Slots'
import DialogActions from '@/components/_custom/DialogActions/DialogActions'

interface DeleteOccasionalSlotDialogProps {
    handleClose: () => void
    occasionalSlots: OccasionalSlot[]
    refetchData: () => void
    occasionalSlotType: CalendarEventType
    closeParentDialog?: () => void
}

const DeleteOccasionalSlotDialog: FunctionComponent<DeleteOccasionalSlotDialogProps> = ({
    handleClose,
    occasionalSlots,
    refetchData,
    occasionalSlotType,
    closeParentDialog
}: DeleteOccasionalSlotDialogProps) => {
    const { t } = useTranslation()

    const { currentDaycare: daycare } = useCurrentDaycareInfos()

    const isSlotReserved: boolean = occasionalSlotType === CalendarEventType.VALIDATED

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [slotsNumber, setSlotsNumber] = useState<string>(occasionalSlots.length.toString())

    const {
        message: daycareComment,
        textAreaProps,
        dialog
    } = usePredefinedMessage(DaycarePredefinedMessageType.cancelOccasionalSlot)

    const [selectChilds, setSelectChilds] = useState<string[]>(
        occasionalSlots.map((slot: OccasionalSlot) => {
            return slot.id.toString()
        })
    )

    const selectOptionsChild: SelectOptionsDeprecated = [
        {
            options: occasionalSlots.map((slot: OccasionalSlot): SelectOptionDeprecated => {
                return {
                    name: slot.childName + (slot.childComplement ? ' - ' + slot.childComplement : ''),
                    value: slot.id.toString()
                }
            })
        }
    ]

    const [deleteOccasionalSlot, { loading: loadingDelete }] = useMutation(DELETE_OCCASIONAL_SLOT, {
        variables: {
            daycare_id: daycare.id,
            occasionalSlots: occasionalSlots
                .filter((slot) => {
                    return selectChilds.includes(slot.id.toString())
                })
                .map((slot) => {
                    return slot.id
                })
                .slice(0, parseInt(slotsNumber)),
            daycareComment: daycareComment ? daycareComment : null
        },
        onCompleted: () => {
            refetchData()
            if (closeParentDialog) {
                closeParentDialog()
            }
            handleClose()

            showNotificationSuccess(t('occasionalSlotsDeleted'))
        },
        onError: () => {
            showNotificationError(t('errorDeletedOccasionalSlots'))
        }
    })

    return (
        <>
            <DialogHeader title={occasionalSlots.length > 1 ? t('deleteOccasionalSlots') : t('deleteOccasionalSlot')} />
            <DialogBody className={styles.DialogBody}>
                <p className={styles.Text}>
                    {occasionalSlots.length > 1 ? t('deleteOccasionalSlotsMessage') : t('deleteOccasionalSlotMessage')}
                </p>
                <p className={styles.Text}>{t('noRollBack')}</p>
                {occasionalSlots.length > 1 &&
                    (occasionalSlotType === CalendarEventType.VALIDATED ? (
                        <SelectMultiple
                            selectOptions={selectOptionsChild}
                            selectValues={selectChilds}
                            setSelectValues={setSelectChilds}
                            className={styles.UserInput}
                        />
                    ) : (
                        <UserInput
                            type={'number'}
                            verify={'number'}
                            label={t('seatsNumber')}
                            value={slotsNumber}
                            setValue={setSlotsNumber}
                            className={styles.UserInput}
                            maxNumber={occasionalSlots.length}
                            minNumber={1}
                        />
                    ))}
                {isSlotReserved && (
                    <div className={styles.TextAreaInput}>
                        <p className={styles.TextAreaTitle}>{t('unbookReason')}</p>
                        <TextArea placeholder={t('unbookReason')} className={styles.UserInput} {...textAreaProps} />
                    </div>
                )}
                <DialogActions>
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('yes')}
                        customIcon={<Delete />}
                        onClick={deleteOccasionalSlot}
                        disabled={loadingDelete}
                        nowrap
                    />
                    <ButtonTextIcon
                        type={'custom'}
                        customText={t('no')}
                        customIcon={<Close />}
                        onClick={handleClose}
                        nowrap
                    />
                </DialogActions>
                {dialog}
            </DialogBody>
        </>
    )
}

export default DeleteOccasionalSlotDialog
